import { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { isHaveActionPermission } from "utills/helpers";
import { DownloadIcon } from "@heroicons/react/outline";
import { groupApi } from "routes/group/api/group.service";
import useQueryParams from "routes/group/hooks/useQueryParams";
import { useGetList } from "routes/group/hooks/useGetList";
import { useGetEvents } from "routes/group/hooks/useGetEvents";
import { useExportExcel } from "routes/group/hooks/useExportExcel";
import { FullScreenLoading } from "components/fullScreenLoading";
import FilterBar, { FilterType } from "routes/group/components/FilterBar";
import DataTable from "routes/group/components/DataTable";

const GroupManagementListPage = () => {
  const PAGE_SIZE = 10;

  const { searchParams, paramsObject, setQueryParams } = useQueryParams();

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const searchQuery = searchParams.get("name_en") ?? "";
  const eventQuery = searchParams.get("event_id") ?? "";
  const orderStatusQuery = searchParams.get("order_status") ?? "";

  const { data, loading, paging, params, setParams, fetchData } = useGetList({
    page: page,
    name_en: searchQuery,
    event_id: eventQuery,
    order_status: orderStatusQuery,
    limit: PAGE_SIZE,
  });

  const { data: events, loading: eventsLoading } = useGetEvents();

  const EVENT_OPTIONS = useMemo(() => {
    if (!events) return [];

    return events?.map((event) => {
      return {
        value: event?.id,
        label: event?.name_en,
      };
    });
  }, [events]);

  const { loading: exportLoading, fetchData: exportFetchData } =
    useExportExcel();

  const [filter, setFilter] = useState<FilterType>({
    page: page,
    name_en: searchQuery,
    event_id: eventQuery,
    order_status: orderStatusQuery,
  });

  const [exportId, setExportId] = useState<string | null>();

  const handleChangeFilter = (key: string, value: string) => {
    setFilter({
      ...filter,
      [key]: value,
    });
  };

  const handleChangePage = (page: number) => {
    setFilter({ ...filter, page: page });
    // Update URL search params
    setQueryParams({
      page: page.toString(),
    });
    setParams({ ...params, page: page }); // Update API params
  };

  const handleSearch = async (data: FilterType) => {
    console.log("Search:", data);

    // Update URL params
    setQueryParams({
      page: "1",
      name_en: filter.name_en,
      event_id: filter.event_id,
      order_status: filter.order_status,
    });

    // Update API params
    setParams({
      page: 1,
      name_en: filter.name_en,
      event_id: filter.event_id,
      order_status: filter.order_status,
      limit: PAGE_SIZE,
    });
  };

  const handleExportExcel = async () => {
    const res = await exportFetchData({
      q: filter.name_en,
      event_id: filter.event_id,
      order_status: filter.order_status,
    });

    if (res.is_success) setExportId(res.export_id);
  };

  // Handle Export Excel
  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        groupApi.getExportResult(exportId).then((data) => {
          const isSuccess = get(data, "data.data.is_success", false);
          if (isSuccess) {
            setExportId(null);
            window.location.href = get(data, "data.data.file_link", "#");
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, groupApi.getExportResult]);

  return (
    <section className="wrapper">
      {(loading || eventsLoading) && <FullScreenLoading />}

      <Breadcrumbs
        customPageLabel="Group Management"
        additionalLabel={
          <div className="flex gap-x-3">
            {isHaveActionPermission("", "group_buy_management", "export") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={() => {
                  handleExportExcel();
                }}
                loading={exportLoading || (exportId ? true : false)}
              >
                <DownloadIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Export Excel</span>
              </Button>
            )}
          </div>
        }
      />

      <FilterBar
        eventOptions={EVENT_OPTIONS}
        filter={filter}
        handleChangeFilter={handleChangeFilter}
        onSubmit={handleSearch}
      />

      <DataTable
        events={events}
        data={data}
        paging={paging}
        handleChangePage={handleChangePage}
      />
    </section>
  );
};

export default GroupManagementListPage;
